import React, { useState } from "react";
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  IconButton,
  MenuItem,
  Icon,
  FormControl,
  Select,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import useUserStore from "store/zustand/user";
import { useTranslation } from "react-i18next";
import { getSvgByLanguage } from "components/Flag";
import useLocalizationStore from "store/zustand/localization";
import { forgotPasswordUserAPI } from "store/services/user.service";
import { createAlert } from "store/utils/alertUtils";

const ResetPassword = () => {
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: "",
    },
  });
  const navigate = useNavigate();
  const { setEmail, setIsForgotPassword } = useUserStore();
  const [submitting, setSubmitting] = useState(false);
  const { t, i18n } = useTranslation();
  const { setLanguage, language } = useLocalizationStore();
  const [selectedLanguage, setSelectedLanguage] = useState(language || "en");

  const handleLanguageChange = async (lang) => {
    setSelectedLanguage(lang);
    setLanguage(lang);
    i18n.changeLanguage(lang);
  };

  const onSubmit = async ({ email }) => {
    try {
      setSubmitting(true);
      const response = await forgotPasswordUserAPI({ email });

      if (response.data.isSuccess) {
        setEmail(email);
        setIsForgotPassword(true);
        navigate("/verify-code");
        createAlert({
          type: "success",
          title: "",
          message: response.data.message,
          notification: {
            title: response.data.message,
            body: response.data.message,
            titleArabic: response.data.message,
            bodyArabic: response.data.message,
          },
          status: true,
        });
      } else {
        createAlert({
          type: "error",
          title: "",
          message: response.data.message,
          notification: {
            title: response.data.message,
            body: response.data.message,
            titleArabic: response.data.message,
            bodyArabic: response.data.message,
          },
          status: true,
        });
      }
    } catch (error) {
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundImage: `url('/images/bg-image-3.jpg')`, // 👈 your image path
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          bgcolor: "rgba(0, 0, 0, 0.3)", // dark overlay
          zIndex: 1,
        },
      }}
    >
      <Box
        component="nav"
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 4,
          py: 2,
          zIndex: 2,
          color: "common.white",
        }}
      >
        <Box
          component="img"
          src="/icon/mausool.png"
          alt="Mausool logo"
          onClick={() => navigate("/")}
          sx={{ height: 40, cursor: "pointer" }}
        />
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton size={"medium"} aria-label="flag">
            <Icon sx={{ width: "1.5rem", height: "1.5rem" }} aria-label="flag">
              {getSvgByLanguage(selectedLanguage)}
            </Icon>
          </IconButton>

          <FormControl
            size="small"
            name="language"
            aria-label="language"
            sx={{
              border: "none", // No border for the FormControl
              outline: "none",
            }}
          >
            <Select
              value={selectedLanguage}
              aria-labelledby="language-select" // Correctly associates aria-labelledby
              onChange={(event) => handleLanguageChange(event.target.value)}
              MenuProps={{
                disableScrollLock: true,
              }}
              sx={{
                padding: 0,
                minWidth: 0,
                border: "none", // No border for the FormControl
                outline: "none", // Remove outline
                color: "white !important", // Set text color to white
                "& .MuiSelect-select": {
                  minWidth: "0 !important", // Make sure select width is minimal
                  color: "white !important", // Set text color to white
                  border: "none", // Remove border from the select input
                },
                "& .MuiOutlinedInput-root": {
                  border: "none", // Remove border from the root input
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: "none", // Remove focus border
                  },
                },
                "& .MuiSelect-icon": {
                  color: "white !important", // Set the arrow icon color
                },
              }}
            >
              <MenuItem value="en">{t("header.language.short.en")}</MenuItem>
              <MenuItem value="ar">{t("header.language.short.ar")}</MenuItem>
            </Select>
          </FormControl>
        </Box>{" "}
      </Box>
      <Paper
        elevation={6}
        sx={{
          width: "100%",
          maxWidth: 500,
          p: 4,
          borderRadius: 1,
          zIndex: 2,
          backdropFilter: "blur(6px)",
          backgroundColor: "rgba(255, 255, 255, 0.85)",
        }}
      >
        <Typography variant="h5" fontWeight={600} mb={1} textAlign="center">
          {t("auth.reset.title")}
        </Typography>
        <Typography
          variant="body2"
          textAlign="center"
          color="text.secondary"
          mb={3}
        >
          {t("auth.reset.description")}
        </Typography>

        <Box component="form" mt={4} onSubmit={handleSubmit(onSubmit)}>
          <TextField
            fullWidth
            label={t("auth.reset.emailLabel")}
            type="email"
            {...register("email", {
              required: t("auth.common.email_required"),
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: t("auth.common.invalid_email"),
              },
            })}
            error={!!errors.email}
            helperText={errors.email?.message || " "}
          />

          <Button
            fullWidth
            variant="contained"
            color="primary"
            type="submit"
            sx={{ mt: 2 }}
            disabled={submitting}
          >
            {t("auth.reset.submit")}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ResetPassword;
